import React, { useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Icon,
  Flex
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiMail, FiLogIn, FiUserPlus } from 'react-icons/fi';
import { useAuth } from '../components/auth';
import { Auth } from '../models/auth';
import { LoginResponse } from '../models/web/login-response';
import {
  ALERT_DEFAULT_ERROR_TITLE,
  DASHBOARD_PAGE_ROUTE,
  LINK_MEMBER_PAGE_ROUTE,
  LINK_COLOR,
  REGISTRATION_PAGE_ROUTE,
  FORGOT_PASSWORD_PAGE
} from '../constants';
import { HttpStatusCode } from '../models/http-status-code';
import AlertMessage from "../components/alert-message";
import { postSendVerificationEmail } from "../services/user-service";
import { HttpRequestResponse } from "../models/web/http-request-response";
import ColorModeToggle from '../components/color-mode-toggle';

function Login(): JSX.Element {
  const [alertDescription, setAlertDescription] = useState<string>();
  const [sendVerificationEmailLink, setSendVerificationElementLink] = useState<any>();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const auth: Auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');

  let from: string = location.state?.from?.pathname ?? '/';

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Mmm... Esto no parece una dirección de correo electrónico válida.')
        .required('Ingresá una dirección de correo electrónico.'),
      password: Yup.string().required('Ingresá una contraseña.')
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      from = from === '/' ? DASHBOARD_PAGE_ROUTE : from;
      const response: HttpRequestResponse<LoginResponse> = await auth.login(values.email, values.password);

      switch (response.statusCode) {
        case HttpStatusCode.Ok:
          const responseContent = (response.response as LoginResponse);
          from = !responseContent.user.selectedMember || !responseContent.user.members
            ? LINK_MEMBER_PAGE_ROUTE
            : from;
          navigate(from, { replace: true });
          break;
        case HttpStatusCode.Unauthorized:
          switch (response.message) {
            case 'Credentials are invalid':
              setAlertDescription('Las credenciales ingresadas son inválidas. Verificalas y probá de nuevo.');
              break;
            case 'User is not active':
              setAlertDescription('La cuenta aún no ha sido verificada. Por favor, revisá tu bandeja de entrada: te mandamos un correo electrónico para verificarla. Si no podés encontrar dicho correo electrónico, podés solicitar uno nuevo haciendo click en el siguiente botón:');
              setSendVerificationElementLink(
                <Button
                  role="link"
                  onClick={async () => await sendVerificationEmail(formik.values.email)}
                  colorScheme="green"
                  variant="outline"
                  size="sm"
                  leftIcon={<FiMail />}
                >
                  Solicitar correo electrónico
                </Button>
              );
              break;
          }
          break;
        default:
          setAlertDescription('¡Ups! Algo salió mal. Intentalo de nuevo más tarde...');
          break;
      }
      setIsLoading(false);
    }
  });

  const sendVerificationEmail = async (email: string): Promise<void> => {
    const response = await postSendVerificationEmail(email);
    if (response.isSuccessful) {
      toast({
        id: 'post-send-verification-email-toast',
        title: '¡Revisá tu bandeja de entrada!',
        description: 'Te enviamos un correo electrónico para que puedas verificar y activar tu cuenta.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
      setAlertDescription("");
    }
  };

  const handleShowPasswordClick = (): void => setShowPassword(!showPassword);

  return (
    <Box
      minH="100vh"
      position="relative"
      py={8}
      className="fade-in-up"
      backgroundImage="url('/pacifico-sede.gif')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgGradient: useColorModeValue(
          'linear(135deg, rgba(247, 250, 252, 0.85) 0%, rgba(240, 253, 244, 0.85) 100%)',
          'linear(135deg, rgba(26, 32, 44, 0.85) 0%, rgba(45, 55, 72, 0.85) 100%)'
        ),
        zIndex: 1
      }}
    >
              <Container maxW="lg" centerContent position="relative" zIndex={2}>
        <Flex
          direction="column"
          align="center"
          w="full"
          mb={8}
        >
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>

          <Card
            w="full"
            maxW="md"
            shadow="2xl"
            bg={cardBg}
            borderRadius="2xl"
            border="1px"
            borderColor={useColorModeValue('green.100', 'gray.700')}
            className="card-hover"
            overflow="hidden"
          >
            <CardHeader
              bg={useColorModeValue('green.500', 'green.600')}
              color="white"
              textAlign="center"
              py={6}
            >
              <VStack spacing={2}>
                <Icon as={FiLogIn} boxSize={8} />
                <Heading size="lg" fontWeight="bold">
                  Iniciar sesión
                </Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Ingresá tus credenciales para continuar
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={8}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={6}>
                  <FormControl
                    isInvalid={
                      (formik.touched.email ?? false) && formik.errors.email != null
                    }
                  >
                    <FormLabel
                      htmlFor="email"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      Correo electrónico
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="tu@email.com"
                        size="lg"
                        borderRadius="lg"
                        focusBorderColor="green.500"
                        _hover={{
                          borderColor: 'green.300'
                        }}
                      />
                      <InputRightElement pointerEvents="none" height="3rem">
                        <Icon as={FiMail} color="gray.400" />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={
                      (formik.touched.password ?? false) &&
                      formik.errors.password != null
                    }
                  >
                    <FormLabel
                      htmlFor="password"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      Contraseña
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="••••••••"
                        size="lg"
                        borderRadius="lg"
                        focusBorderColor="green.500"
                        _hover={{
                          borderColor: 'green.300'
                        }}
                      />
                      <InputRightElement height="3rem">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleShowPasswordClick}
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          p={2}
                        >
                          <Icon as={showPassword ? FiEyeOff : FiEye} />
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="green"
                    size="lg"
                    borderRadius="lg"
                    isLoading={isLoading}
                    loadingText="Ingresando..."
                    leftIcon={<FiLogIn />}
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                  >
                    Ingresar
                  </Button>

                  <AlertMessage
                    status="error"
                    title={ALERT_DEFAULT_ERROR_TITLE}
                    description={alertDescription}
                    hidden={!alertDescription}
                    additionalDescriptionElement={sendVerificationEmailLink}
                  />
                </Stack>
              </form>
            </CardBody>
          </Card>

          <VStack spacing={4} mt={8} textAlign="center">
            <Text color={textColor}>
              <Link
                as={RouterLink}
                to={FORGOT_PASSWORD_PAGE}
                color={LINK_COLOR}
                fontWeight="semibold"
                _hover={{
                  textDecoration: 'none',
                  color: useColorModeValue('green.600', 'green.300')
                }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Text>

            <Divider />

            <VStack spacing={2}>
              <Text color={headingColor} fontWeight="semibold" fontSize="lg">
                ¿No tenés una cuenta?
              </Text>
              <Text color={textColor}>
                Podés crear una de manera simple y rápida
              </Text>
              <Button
                as={RouterLink}
                to={REGISTRATION_PAGE_ROUTE}
                variant="outline"
                colorScheme="green"
                size="md"
                leftIcon={<FiUserPlus />}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md'
                }}
              >
                Crear cuenta
              </Button>
            </VStack>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}

export default Login;
